<template>
  <!-- Backdrop -->
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="isOpen"
           class="fixed inset-0 bg-black/30 z-40"
           @click="handleBackdropClick"></div>
    </Transition>

    <!-- Floating Cart Container -->
    <Transition name="slide-down">
      <div v-if="isOpen"
           class="fixed top-[64px] right-0 w-full md:w-96 max-h-screen flex flex-col bg-card border-l border-border z-50 shadow-xl">
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b border-border">
          <h2 class="text-lg font-bold text-foreground flex items-center gap-2">
            <font-awesome-icon :icon="['fal', 'shopping-cart']"
                               class="text-primary" />
            Shopping Cart
          </h2>
          <button class="p-2 text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-primary/10"
                  aria-label="Close cart"
                  @click="handleClose">
            <font-awesome-icon :icon="['fal', 'times']"
                               class="text-lg" />
          </button>
        </div>

        <!-- Items List (Scrollable) -->
        <div class="flex-1 overflow-y-auto">
          <CartEmpty v-if="cartStore.isEmpty" />
          <TransitionGroup v-else
                           name="list"
                           tag="div"
                           class="divide-y divide-border">
            <CartItemRow v-for="item in cartStore.cartItems"
                         :key="item.productId"
                         :item="item"
                         :product="getProductDetails(item.productId!)"
                         @delete="handleDeleteItem" />
          </TransitionGroup>
        </div>

        <!-- Summary -->
        <CartSummary v-if="!cartStore.isEmpty" />

        <!-- Actions -->
        <CartActions @close="handleClose" />
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useCartStore } from '@/stores/cartStore';
import { useToast } from '@/composables/useToast';
import CartEmpty from '@/components/cart/CartEmpty.vue';
import CartItemRow from '@/components/cart/CartItemRow.vue';
import CartSummary from '@/components/cart/CartSummary.vue';
import CartActions from '@/components/cart/CartActions.vue';
import type { IProductRequest } from '@/data/_autogen/IValveVista';

interface Props {
  isOpen: boolean
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: []
}>();

const cartStore = useCartStore();
const { error } = useToast();

const getProductDetails = (productId: number): IProductRequest => {
  return (
    cartStore.getProductById(productId) || {
      id: productId,
      displayName: 'Unknown Product',
      price: 0,
    }
  );
};

const handleClose = () => {
  emit('close');
};

const handleBackdropClick = () => {
  emit('close');
};

const handleDeleteItem = async (productId: number) => {
  try {
    await cartStore.removeItem(productId);
  } catch (err) {
    error('Failed to remove item from cart');
    console.error('Delete item error:', err);
  }
};
</script>

<style scoped>
/* Fade animation for backdrop */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide down animation for cart */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
}

/* List item animations */
.list-enter-active,
.list-leave-active {
  transition: all 0.3s ease;
}

.list-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.list-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

.list-move {
  transition: transform 0.3s ease;
}
</style>
